import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #isAnimating = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#preventDefaultDetailsToggle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Prevent the default details toggle behavior to avoid double-toggling
   */
  #preventDefaultDetailsToggle() {
    const summary = this.refs.details.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', (event) => {
        event.preventDefault();
      });
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open') && !this.#isAnimating;
  }

  /**
   * @returns {boolean} Whether the main menu drawer is closed
   */
  get isClosed() {
    return !this.refs.details.hasAttribute('open') && !this.#isAnimating;
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    // Prevent toggling during animations
    if (this.#isAnimating) return;

    // Use a direct check of the open attribute to determine state
    const shouldOpen = !this.refs.details.hasAttribute('open');

    if (shouldOpen) {
      this.open(event);
    } else {
      this.close();
    }
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;
    if (details.hasAttribute('open')) return; // Already open

    this.#isAnimating = true;
    summary.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');

    const accordionContent = this.#disableAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      onAnimationEnd(details, () => {
        this.#isAnimating = false;
        this.#enableAccordionAnimations(accordionContent);
        trapFocus(details);
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;
    if (!details.hasAttribute('open')) return; // Already closed

    this.#isAnimating = true;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      this.#isAnimating = false;
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        queueMicrotask(() => {
          trapFocus(this.refs.details);
        });
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Disable accordion animations to prevent unwanted transitions when the drawer opens
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   * @returns {NodeListOf<Element>} The accordion content elements
   */
  #disableAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    return content;
  }

  /**
   * Re-enable accordion animations after drawer open animation completes
   * @param {NodeListOf<Element>} content - The accordion content elements
   */
  #enableAccordionAnimations(content) {
    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.remove('details-content--no-animation');
      }
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
